import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import CliAuthShell from "@/components/cliAuth/CliAuthShell";
import CliAuthStatusCard from "@/components/cliAuth/CliAuthStatusCard";
import { getEnv } from "@/utils/env";
import {
  MAKO_CLI_CALLBACK_URL_KEY,
  MAKO_CLI_FLOW_KEY,
  MAKO_CLI_NONCE_KEY,
  validateCliCallbackUrl,
  validateCliNonce,
} from "@/utils/cliAuth";

async function fetchHandoffCode({ apiBaseUrl, provider, params }: { apiBaseUrl: string; provider: string; params: Record<string, string> }) {
  const url = new URL(`${apiBaseUrl}/auth/cli/${provider}/callback`);
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    url.searchParams.set(key, value as string);
  }

  const resp = await fetch(url.toString(), { credentials: "include" });
  if (!resp.ok) {
    throw new Error(`Callback failed (${resp.status})`);
  }
  const body = await resp.json();
  if (!body?.handoff_code) {
    throw new Error("Callback did not return handoff_code");
  }
  return body.handoff_code;
}

export const Route = createFileRoute("/auth/$provider/bearer/callback")({
  component: BearerCallbackPage,
  staticData: { noLayout: true },
});

function BearerCallbackPage() {
  const { provider } = Route.useParams();
  const [error, setError] = useState<string | null>(null);
  const [handoffCode, setHandoffCode] = useState<string | null>(null);
  const [isManual, setIsManual] = useState(false);
  const [copyState, setCopyState] = useState("idle");

  useEffect(() => {
    if (copyState === "idle") return;
    const timer = setTimeout(() => setCopyState("idle"), 2000);
    return () => clearTimeout(timer);
  }, [copyState]);

  const handleCopy = async () => {
    if (!handoffCode) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(handoffCode);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = handoffCode;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.top = "-1000px";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopyState("success");
    } catch (err) {
      console.error(err);
      setCopyState("error");
    }
  };

  useEffect(() => {
    if (!provider) {
      setError("Missing OAuth provider.");
      return;
    }

    const flow = window.sessionStorage.getItem(MAKO_CLI_FLOW_KEY);
    const manualFlow = flow === "manual";
    setIsManual(manualFlow);

    const callbackUrl = window.sessionStorage.getItem(MAKO_CLI_CALLBACK_URL_KEY);
    const nonce = window.sessionStorage.getItem(MAKO_CLI_NONCE_KEY);

    const nonceValidation = validateCliNonce(nonce);
    if (!nonceValidation.ok) {
      setError((nonceValidation as { error: string }).error);
      return;
    }

    if (!manualFlow) {
      const callbackValidation = validateCliCallbackUrl(callbackUrl);
      if (!callbackValidation.ok) {
        setError((callbackValidation as { error: string }).error);
        return;
      }
    }

    const apiBaseUrl = getEnv().NEXT_PUBLIC_CLIENT_SIDE_API_URL;
    if (!apiBaseUrl) {
      setError("Missing NEXT_PUBLIC_CLIENT_SIDE_API_URL.");
      return;
    }

    // Get remaining search params from the URL (excluding provider which is a path param)
    const params = Object.fromEntries(
      new URLSearchParams(window.location.search)
    );

    fetchHandoffCode({ apiBaseUrl, provider, params })
      .then((handoffCode) => {
        window.sessionStorage.removeItem(MAKO_CLI_CALLBACK_URL_KEY);
        window.sessionStorage.removeItem(MAKO_CLI_NONCE_KEY);
        window.sessionStorage.removeItem(MAKO_CLI_FLOW_KEY);

        if (manualFlow) {
          setHandoffCode(handoffCode);
          return;
        }

        const callbackValidation = validateCliCallbackUrl(callbackUrl);
        if (!callbackValidation.ok) {
          setError((callbackValidation as { error: string }).error);
          return;
        }

        const fragment = `handoff_code=${encodeURIComponent(
          handoffCode
        )}&nonce=${encodeURIComponent(nonce!)}`;
        window.location.replace(`${callbackValidation.normalized}#${fragment}`);
      })
      .catch((e) => {
        console.error(e);
        setError("Unable to complete OAuth login.");
      });
  }, [provider]);

  const isError = Boolean(error);
  const showManualCode = isManual && handoffCode && !isError;
  const statusTitle = isError
    ? "Unable to finish login"
    : showManualCode
    ? "Login complete"
    : "Completing sign-in";
  const statusMessage = isError
    ? error!
    : showManualCode
    ? "Copy the code below and paste it into your terminal."
    : "Completing login…";
  const statusTone = isError ? "error" : showManualCode ? "success" : "info";
  const helperText = isError
    ? "Return to the CLI and try again."
    : showManualCode
    ? "You may close this tab after pasting the code."
    : "This window will update when the login finishes.";
  const showCopySuccess = copyState === "success";
  const showCopyError = copyState === "error";

  return (
    <CliAuthShell>
      <CliAuthStatusCard
        tone={statusTone}
        title={statusTitle}
        message={statusMessage}
      />
      {showManualCode ? (
        <div className="mt-4 rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-800">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-slate-600">Login code</div>
            <div className="flex items-center gap-2">
              {showCopySuccess ? (
                <span className="text-[10px] font-semibold text-green-600">
                  Copied
                </span>
              ) : null}
              {showCopyError ? (
                <span className="text-[10px] font-semibold text-red-600">
                  Copy failed
                </span>
              ) : null}
              <button
                type="button"
                onClick={handleCopy}
                className="rounded-md border border-slate-200 bg-white p-1 text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-200"
                aria-label="Copy login code"
                title="Copy login code"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="h-4 w-4"
                  aria-hidden="true"
                >
                  <rect x="6" y="6" width="10" height="10" rx="2" />
                  <rect x="2" y="2" width="10" height="10" rx="2" opacity="0.35" />
                </svg>
              </button>
            </div>
          </div>
          <div className="mt-1 break-all font-mono">{handoffCode}</div>
        </div>
      ) : null}
      <p className="mt-4 text-center text-xs text-grey-200">{helperText}</p>
    </CliAuthShell>
  );
}
